import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Scissors } from 'lucide-react';
import InfoTip from '@/components/InfoTip';
import HomeCurrencyDividendCard from '@/components/HomeCurrencyDividendCard';
import SwipeHint, { useSwipeHint } from '@/components/SwipeHint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_META = {
  king:       { emoji: '\u{1F451}', label: 'King' },
  aristocrat: { emoji: '\u{1F3C6}', label: 'Aristocrat' },
  achiever:   { emoji: '\u{1F396}\uFE0F', label: 'Achiever' },
};

/**
 * TierCutsRow — Round 95f (Jun 2026). "Cut from List" companion to
 * StreakAtRiskRow: members REMOVED from the curated Kings /
 * Aristocrats / Achievers lists this quarter (dividend cut, a freeze
 * finally broke the streak, or an index change).
 *
 * Fed by /screener/tier-cuts (db.tier_list_cuts — auto-populated when
 * the quarterly tier refresh strips a former member, or by the audit
 * workflow). NULL-RENDERS when empty by design: the box only appears
 * when a cut actually happens.
 *
 * Usage (Dashboard, under StreakAtRiskRow):
 *   <TierCutsRow onStockClick={handleStockClick} ownedTickers={ownedTickers} />
 * Usage (Challenge pages, scoped):
 *   <TierCutsRow onStockClick={handleStockClick} tierFilter="king" />
 */
export default function TierCutsRow({ onStockClick, ownedTickers = [], tierFilter = null }) {
  const [items, setItems] = useState(null);
  const swipeRef = useRef(null);
  const [swipeHint, updateSwipeHint] = useSwipeHint(swipeRef, [tierFilter, items]);
  const [openFxTicker, setOpenFxTicker] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/screener/tier-cuts`);
        if (cancelled) return;
        const raw = Array.isArray(res.data?.items) ? res.data.items : [];
        const filtered = tierFilter
          ? raw.filter((it) => (it.former_tier || '').toLowerCase() === String(tierFilter).toLowerCase())
          : raw;
        setItems(filtered);
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [tierFilter]);

  if (!items || items.length === 0) return null;

  const ownedSet = new Set((ownedTickers || []).map((t) => String(t).toUpperCase()));

  return (
    <div
      className="mb-3 rounded-lg border border-rose-400/40 bg-gradient-to-r from-rose-50/70 via-red-50/40 to-transparent dark:from-rose-950/25 dark:via-red-950/15 dark:to-transparent px-3 py-2"
      data-testid="tier-cuts-row"
    >
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <Scissors className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400 shrink-0" />
        <span
          className="text-[13px] font-black text-rose-600 dark:text-rose-400 tabular-nums leading-none"
          data-testid="tier-cuts-count"
          title={`${items.length} ticker${items.length === 1 ? '' : 's'} removed from the curated Kings / Aristocrats / Achievers lists this quarter`}
        >
          {items.length}
        </span>
        <span className="text-[11px] font-bold uppercase tracking-wider text-rose-900 dark:text-rose-200">
          Cut From List
        </span>
        <span className="text-[11px] text-muted-foreground">
          Streak confirmed broken — a calendar year's regular payouts didn't top the prior year, or the payout was reduced
        </span>
        <InfoTip
          content="Companies that lost their place on the curated dividend tier lists — usually a dividend cut, or a freeze that finally broke the multi-year raise streak. The badge is already gone from their stock rows; this box stays up until the next quarterly list refresh so you can review your exposure."
          maxWidth={300}
        />
      </div>

      <div ref={swipeRef} onScroll={updateSwipeHint} className="flex items-stretch gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
        {items.map((s) => {
          const meta = TIER_META[(s.former_tier || '').toLowerCase()] || { emoji: '', label: '' };
          const isForeignAdr = !!s.is_foreign_adr;
          const streak = s.former_streak_years ? ` · was ${s.former_streak_years}y streak` : '';
          const tooltip = isForeignAdr
            ? `${s.name || s.ticker}: former ${meta.label}${streak} — click to check the home-currency streak.`
            : `${s.name || s.ticker}: former ${meta.label}${streak} — ${s.reason || 'removed from the curated list'} (${s.quarter || ''})`;
          const isOwned = ownedSet.has(String(s.ticker).toUpperCase());
          const isOpen = openFxTicker === s.ticker;
          const chipCls = isForeignAdr
            ? (isOpen
                ? 'bg-amber-100 dark:bg-amber-900/60 border border-amber-500/80 dark:border-amber-500/70 ring-1 ring-amber-400/50'
                : 'bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-950/60 border border-amber-400/60 dark:border-amber-600/50')
            : (isOwned
                ? 'bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-950/60 border border-amber-400/70 dark:border-amber-600/60 ring-1 ring-amber-400/40'
                : 'bg-white/70 dark:bg-zinc-900/70 hover:bg-rose-100 dark:hover:bg-rose-950/60 border border-rose-300/60 dark:border-rose-700/50');
          const labelCls = isForeignAdr
            ? 'text-amber-700 dark:text-amber-300 line-through decoration-amber-500/70'
            : 'text-rose-700 dark:text-rose-300 line-through decoration-rose-500/70';
          const handleClick = () => {
            if (isForeignAdr) {
              setOpenFxTicker((cur) => (cur === s.ticker ? null : s.ticker));
            } else if (onStockClick) {
              onStockClick(s.ticker);
            }
          };
          return (
            <button
              key={`${s.ticker}-${s.quarter}`}
              type="button"
              onClick={handleClick}
              title={tooltip}
              data-testid={`tier-cut-chip-${s.ticker}`}
              data-owned={isOwned ? 'true' : 'false'}
              data-fx-review={isForeignAdr ? 'true' : 'false'}
              data-fx-open={isOpen ? 'true' : 'false'}
              className={`shrink-0 flex flex-col items-start gap-0.5 px-2 py-1 rounded-md transition-colors ${chipCls}`}
            >
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold">
                <span className="text-[11px]" aria-hidden>{meta.emoji}</span>
                <span className="font-mono font-bold text-foreground">{s.ticker}</span>
                <span className={labelCls}>
                  {meta.label}
                </span>
                {isForeignAdr && (
                  <span
                    className="ml-1 px-1 py-[1px] rounded bg-amber-200/70 dark:bg-amber-800/60 text-amber-900 dark:text-amber-100 text-[9px] font-bold tracking-wider uppercase"
                    title="Foreign OTC/ADR — click to see the home-currency streak"
                  >
                    FX?
                  </span>
                )}
              </span>
              <span className="text-[10px] font-mono text-muted-foreground/90 leading-none">
                {s.former_streak_years ? `was ${s.former_streak_years}y streak` : (s.quarter || 'removed')}
                {isOwned ? ' · you own this' : ''}
              </span>
            </button>
          );
        })}
      </div>
      <SwipeHint hint={swipeHint} testId="tier-cuts-swipe-hint" />

      {openFxTicker && (
        <div data-testid={`tier-cut-fx-expansion-${openFxTicker}`}>
          <HomeCurrencyDividendCard ticker={openFxTicker} compact={false} assumeForeign />
          {onStockClick && (
            <button
              type="button"
              onClick={() => onStockClick(openFxTicker)}
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-primary hover:underline"
              data-testid="tier-cut-fx-open-modal"
            >
              Open {openFxTicker} in full modal →
            </button>
          )}
        </div>
      )}

      {items.some((s) => s.is_foreign_adr) && (
        <div className="mt-1.5 text-[11px] font-bold text-muted-foreground/80 flex items-start gap-1">
          <span className="inline-flex items-center gap-1 px-1 py-[1px] rounded bg-amber-200/70 dark:bg-amber-800/60 text-amber-900 dark:text-amber-100 font-bold text-[9px] uppercase tracking-wider shrink-0">
            FX?
          </span>
          <span>
            Foreign OTC / ADR — USD payout dipped due to currency conversion. Click any amber chip to check the home-currency streak. Home-currency streak may still be intact; verify in the parent company&apos;s native currency before treating as a real cut.
          </span>
        </div>
      )}
    </div>
  );
}
